import './tailwind.css'
import { useContext, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import { isElectron } from './utils/platform.js'
import { useFullscreen } from './hooks/useFullscreen.js'
import Menu from './components/Menu.jsx'
import CmdMenu from './components/CmdMenu/CmdMenu.jsx'
import { AppContext } from './components/AppContext.tsx'
import SettingsPage from './pages/SettingsPage.jsx'
import UsagePage from './pages/UsagePage.jsx'
import ConfirmationModal from './components/ConfirmationModal.jsx'

// Keybinding system components
import { KeybindingProvider } from './hooks/keybinding/KeybindingContext.jsx'
import KeySequenceIndicator from './components/KeySequenceIndicator.jsx'
import KeybindingDebugger from './components/KeybindingDebugger.jsx'
import AppKeybindings from './components/AppKeybindings.jsx'
import ConversationPage from './pages/ConversationPage.tsx'
import Screensaver from './components/Screensaver.jsx'
import TabBar from './components/TabBar.tsx'
import { TabProvider } from './components/TabContext.tsx'

function App({ showSettings = false, showUsage = false }) {
  const {
    supabaseClient,
    searchModalActive,
    menuIsOpen,
    onMenuIsOpenChange,
    onAuthSessionUpdate,
    favoriteConversations,
    conversations,
    selectConversation,
    clearConversation,
    isNewConversationIntent,
    confirmationModal,
    hideConfirmation,
  } = useContext(AppContext)

  const { conversationId: paramId } = useParams()
  const navigate = useNavigate()
  const navigatingRef = useRef(false)

  const isFullScreen = useFullscreen()

  // Refs - declare before using in hooks
  const appRef = useRef(null)

  // Keep refs to avoid re-triggering the URL-sync effect when these change
  const selectConversationRef = useRef(selectConversation)
  selectConversationRef.current = selectConversation
  const clearConversationRef = useRef(clearConversation)
  clearConversationRef.current = clearConversation
  const conversationsRef = useRef(conversations)
  conversationsRef.current = conversations
  const favoriteConversationsRef = useRef(favoriteConversations)
  favoriteConversationsRef.current = favoriteConversations

  // Handle URL parameter changes for conversation sync
  // Only re-run when the paramId in the URL actually changes
  useEffect(() => {
    if (showSettings) return // Don't sync conversations on settings page

    if (isNewConversationIntent.current) {
      isNewConversationIntent.current = false
      return
    }

    if (paramId) {
      const exists = conversationsRef.current.some(c => c.conversationId === paramId) ||
        favoriteConversationsRef.current.some(c => c.conversationId === paramId)

      if (exists) {
        selectConversationRef.current(paramId)
      } else {
        if (!navigatingRef.current) {
          clearConversationRef.current()
          navigate('/chat', { replace: true })
          navigatingRef.current = true;
        } else {
          navigatingRef.current = false;
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramId, showSettings, navigate])

  // Check for an existing session and set up auth state listener
  // Using a separate ref to avoid duplicate auth updates
  const authInitializedRef = useRef(false)

  useEffect(() => {
    // Skip if we've already initialized auth
    if (authInitializedRef.current) {
      return
    }

    const initializeAuth = async () => {
      // Mark as initialized to prevent duplicate calls
      authInitializedRef.current = true

      // Check for an existing session
      try {
        const { data: { session } } = await supabaseClient.auth.getSession()
        if (session?.user) {
          onAuthSessionUpdate(session)
        }
      } catch (err) {
        // Reset flag to allow retry
        authInitializedRef.current = false
      }
    }

    initializeAuth()

    // Set up auth state change listener (separate from initial check)
    const { data: listener } = supabaseClient.auth.onAuthStateChange((event, session) => {
      // Only update if the state is different
      if (event !== 'INITIAL_SESSION') {
        onAuthSessionUpdate(session ?? null)
      }
    })

    return () => {
      listener.subscription.unsubscribe()
    }
  }, [supabaseClient, onAuthSessionUpdate])

  // UI event handlers
  const toggleMenu = useCallback(() => {
    onMenuIsOpenChange(!menuIsOpen)
  }, [menuIsOpen, onMenuIsOpenChange])

  return (
    <KeybindingProvider>
      {/* Keybinding system UI components */}
      <KeySequenceIndicator />
      <KeybindingDebugger />
      <AppKeybindings />
      
      {/* Screensaver - activates after 10 minutes of inactivity */}
      <Screensaver />

      <div id="portal-root"></div>

      <div ref={appRef} className="min-h-screen-mobile w-full bg-bg-page text-primary">
        {/* Electron: draggable title bar region (38px) for window dragging */}
        {isElectron() && !isFullScreen && (
          <div
            className="fixed left-0 right-0 top-0 z-50 h-[38px] bg-transparent"
            style={{ WebkitAppRegion: 'drag' }}
            aria-hidden
          />
        )}
        {/* Command Menu at root level */}
        {searchModalActive && <CmdMenu />}
        <div className="h-screen-mobile flex w-full relative overflow-hidden overflow-x-hidden">
          <Menu />
          {/* Mobile backdrop overlay */}
          {menuIsOpen && (
            <div
              className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10 transition-opacity duration-300"
              onClick={toggleMenu}
              aria-label="Close menu"
            />
          )}
          {/* Spacer for menu when open - desktop only */}
          <div className={`hidden md:block transition-all duration-300 ease-in-out ${menuIsOpen ? 'w-[244px]' : 'w-0'}`} />
          {/* Main Content: 38px top margin in Electron for traffic lights, else 8px */}
          <div
            className={`${isElectron() && !isFullScreen ? 'mt-[38px]' : 'md:mt-[8px]'} ${menuIsOpen ? 'md:mb-[8px] md:mr-[8px]' : 'md:mb-[8px] md:mr-[8px] md:ml-[48px]'} md:rounded-[8px] md:border md:border-border-default bg-bg-panel flex-1 min-w-0 overflow-hidden transition-all duration-300 ease-in-out`}
          >
            {/* Conditionally render Settings, Usage, or Chat */}
            {showSettings ? (
              <div className="w-full h-full overflow-y-auto">
                <SettingsPage />
              </div>
            ) : showUsage ? (
              <div className="w-full h-full overflow-y-auto">
                <UsagePage />
              </div>
            ) : (
              <TabProvider>
                <div className="w-full h-full flex flex-col overflow-hidden">
                  <TabBar />
                  <div className="flex-1 min-h-0 overflow-auto">
                    <ConversationPage />
                  </div>
                </div>
              </TabProvider>
            )}
          </div>
        </div>
      </div>

      {/* Global Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.confirmText}
        cancelText={confirmationModal.cancelText}
        variant={confirmationModal.variant}
        isLoading={confirmationModal.isLoading}
        onConfirm={confirmationModal.onConfirm}
        onCancel={confirmationModal.onCancel}
        onClose={hideConfirmation}
      />
    </KeybindingProvider>
  );
}

export default App;
